<template>
  <div class="hiprint-test">
    <a-card title="hiprint 功能测试" :bordered="false">
      <div slot="extra">
        <a-button type="primary" @click="testAll">一键测试全部功能</a-button>
      </div>
      
      <!-- 测试按钮组 -->
      <a-space style="margin-bottom: 20px;">
        <a-button @click="testInit">1. 测试初始化</a-button>
        <a-button @click="testCreateTemplate">2. 创建模板</a-button>
        <a-button @click="testLoadTemplate">3. 加载模板JSON</a-button>
        <a-button @click="testRender">4. 渲染预览</a-button>
        <a-button @click="testGetHtml">5. 获取HTML</a-button>
        <a-button @click="testPrint">6. 打印测试</a-button>
        <a-button @click="testGetJson">7. 获取模板JSON</a-button>
      </a-space>

      <!-- 测试结果 -->
      <a-divider>测试结果</a-divider>
      <a-alert v-if="testResults.length === 0" message="点击上方按钮开始测试" type="info" show-icon />
      <div v-for="(result, index) in testResults" :key="index" style="margin-bottom: 10px;">
        <a-alert 
          :message="result.name" 
          :description="result.details"
          :type="result.success ? 'success' : 'error'" 
          show-icon 
          closable
        />
      </div>

      <!-- 预览区域 -->
      <a-divider>模板预览</a-divider>
      <div ref="previewContainer" class="preview-container">
        <a-empty v-if="!previewHtml" description="暂无预览内容" />
        <div v-else v-html="previewHtml"></div>
      </div>

      <!-- JSON 显示 -->
      <a-divider>模板 JSON</a-divider>
      <a-textarea 
        v-model="templateJsonStr" 
        :rows="15" 
        placeholder="模板JSON内容"
        style="width: 100%; font-family: monospace;"
      />
    </a-card>
  </div>
</template>

<script>
export default {
  name: 'HiprintTest',
  data() {
    return {
      hiprintInstance: null,
      templateInstance: null,
      testResults: [],
      previewHtml: '',
      templateJsonStr: '',
      // 测试模板
      testTemplate: {
        panels: [{
          index: 0,
          name: 1,
          height: 100,
          width: 200,
          printElements: [{
            options: {
              left: 50,
              top: 20,
              height: 20,
              width: 100,
              field: 'title',
              title: '标题',
              fontSize: 16,
              textAlign: 'center'
            },
            printElementType: {
              title: '标题',
              type: 'text'
            }
          }],
          paperNumberLeft: 0,
          paperNumberTop: 0
        }]
      },
      // 测试数据
      testData: {
        title: '测试单据标题',
        number: 'NO.20240101001',
        date: '2024-01-01',
        items: [
          { name: '商品1', qty: 10, price: 100 },
          { name: '商品2', qty: 20, price: 200 }
        ]
      }
    }
  },
  methods: {
    addResult(name, success, details) {
      this.testResults.unshift({ name, success, details: typeof details === 'string' ? details : JSON.stringify(details, null, 2) })
    },

    // 测试1: 初始化
    testInit() {
      try {
        if (window.hiprint) {
          this.addResult('hiprint初始化', true, `版本: ${window.hiprint.version}`)
        } else {
          this.addResult('hiprint初始化', false, 'window.hiprint 不存在')
        }
        
        // 检查关键类
        if (window.hiprint && window.hiprint.PrintTemplate) {
          this.addResult('PrintTemplate类', true, '可用')
        } else {
          this.addResult('PrintTemplate类', false, '不存在')
        }
      } catch (e) {
        this.addResult('初始化', false, e.message)
      }
    },

    // 测试2: 创建模板实例
    testCreateTemplate() {
      try {
        if (!window.hiprint || !window.hiprint.PrintTemplate) {
          this.addResult('创建模板', false, 'hiprint未初始化')
          return
        }
        
        this.templateInstance = new window.hiprint.PrintTemplate({
          template: this.testTemplate
        })
        
        this.addResult('创建模板实例', true, '成功创建 PrintTemplate')
      } catch (e) {
        this.addResult('创建模板', false, e.message)
      }
    },

    // 测试3: 加载模板JSON
    testLoadTemplate() {
      try {
        this.templateJsonStr = JSON.stringify(this.testTemplate, null, 2)
        
        // 尝试从JSON字符串创建
        if (window.hiprint && window.hiprint.PrintTemplate) {
          this.templateInstance = new window.hiprint.PrintTemplate({
            template: this.testTemplate
          })
          this.addResult('加载模板JSON', true, '成功解析并加载模板JSON')
        } else {
          this.addResult('加载模板JSON', false, 'hiprint未初始化')
        }
      } catch (e) {
        this.addResult('加载模板JSON', false, e.message)
      }
    },

    // 测试4: 渲染预览
    testRender() {
      try {
        if (!this.templateInstance) {
          this.addResult('渲染预览', false, '请先创建模板')
          return
        }
        
        // getHtml 返回 DOM 元素数组，需要获取 innerHTML
        const htmlElements = this.templateInstance.getHtml(this.testData)
        if (htmlElements && htmlElements[0]) {
          this.previewHtml = htmlElements[0].innerHTML
          this.addResult('渲染预览', true, `生成HTML长度: ${this.previewHtml.length} 字符`)
        } else {
          this.addResult('渲染预览', false, '未返回HTML元素')
        }
      } catch (e) {
        this.addResult('渲染预览', false, e.message)
      }
    },

    // 测试5: 获取HTML
    testGetHtml() {
      try {
        if (!this.templateInstance) {
          this.addResult('获取HTML', false, '请先创建模板')
          return
        }
        
        // getHtml 返回 DOM 元素数组
        const htmlElements = this.templateInstance.getHtml(this.testData)
        if (htmlElements && htmlElements[0]) {
          const html = htmlElements[0].innerHTML
          console.log('生成的HTML:', html)
          this.addResult('获取HTML', true, `HTML长度: ${html.length}，请查看控制台`)
        } else {
          this.addResult('获取HTML', false, '未返回HTML元素')
        }
      } catch (e) {
        this.addResult('获取HTML', false, e.message)
      }
    },

    // 测试6: 打印
    testPrint() {
      try {
        if (!this.templateInstance) {
          this.addResult('打印测试', false, '请先创建模板')
          return
        }
        
        // 打开打印预览
        this.templateInstance.print(this.testData)
        this.addResult('打印测试', true, '已调用print()方法')
      } catch (e) {
        this.addResult('打印测试', false, e.message)
      }
    },

    // 测试7: 获取模板JSON
    testGetJson() {
      try {
        if (!this.templateInstance) {
          this.addResult('获取模板JSON', false, '请先创建模板')
          return
        }
        
        const json = this.templateInstance.getJson()
        this.templateJsonStr = JSON.stringify(json, null, 2)
        this.addResult('获取模板JSON', true, `JSON长度: ${JSON.stringify(json).length} 字符`)
      } catch (e) {
        this.addResult('获取模板JSON', false, e.message)
      }
    },

    // 一键测试全部
    testAll() {
      this.testResults = []
      this.testInit()
      this.testCreateTemplate()
      this.testLoadTemplate()
      this.testRender()
      this.testGetHtml()
      this.testGetJson()
      this.addResult('全部测试', true, '完成！（打印测试需手动触发）')
    }
  }
}
</script>

<style scoped>
.hiprint-test {
  padding: 20px;
}

.preview-container {
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 20px;
  min-height: 200px;
  background: #fff;
}
</style>
